import { ChangeEvent, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import AstroTipContent from '@/models/astro-tip-content';
import { useAstroTips } from '@/services/astro-tips';
import AstroTipModal from './components/astro-tip-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';

const headlinesCount = (row: AstroTipContent): number => Object.keys(row.headlines ?? {}).length;

const valueFor = (row: AstroTipContent, key: string): string | number => {
    switch (key) {
        case 'active':
            return row.active ? 1 : 0;
        case 'headlines':
            return headlinesCount(row);
        case 'dos':
            return row.dos?.length ?? 0;
        case 'donts':
            return row.donts?.length ?? 0;
        default: {
            const v = (row as unknown as Record<string, unknown>)[key];
            return (v as string | number) ?? '';
        }
    }
};

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    return (a: AstroTipContent, b: AstroTipContent) => {
        const av = valueFor(a, orderBy);
        const bv = valueFor(b, orderBy);
        if (av < bv) return order === 'desc' ? 1 : -1;
        if (av > bv) return order === 'desc' ? -1 : 1;
        return 0;
    };
};

const AstroTips = () => {
    const { data: astroTips, isLoading } = useAstroTips();
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filtered, setFiltered] = useState<AstroTipContent[]>(astroTips?.data || []);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedAstroTipId, setSelectedAstroTipId] = useState<string>();

    useEffect(() => {
        if (astroTips?.data) {
            setFiltered(astroTips.data);
        }
    }, [astroTips]);

    // Clamp so a mutation/search that shrinks the list never strands the user on
    // an out-of-range (empty) page — without surprising them with a reset on a
    // benign background refetch.
    const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));

    const toggle = () => setIsOpen((prev) => !prev);

    const handleRequestAdd = () => {
        setSelectedAstroTipId(undefined);
        toggle();
    };

    const handleRequestSort = (_: unknown, property: string) => {
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }
        setOrder(orderTemp);
        setOrderBy(property);
    };

    const handleClick = (id: string) => {
        setSelectedAstroTipId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => setPage(item);
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Astro Tips</h3>
                    <h3 className="page-subhead subhead">
                        Daily-reading copy pools — override the built-in course-33 reading text without a deploy. Empty fields fall back to the built-in
                        copy.
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Name"
                                            type="text"
                                            onChange={(e) => {
                                                const q = e.target.value.toLowerCase();
                                                setFiltered(astroTips?.data?.filter((p) => p.name.toLowerCase().includes(q)) || []);
                                            }}
                                        />
                                        <MatTableToolbar onRequestAdd={handleRequestAdd} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filtered
                                                    .slice()
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage)
                                                    .map((d) => (
                                                        <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                            <TableCell component="th" scope="row" padding="none">
                                                                {d.name}
                                                            </TableCell>
                                                            <TableCell>{d.active ? 'Active' : 'Inactive'}</TableCell>
                                                            <TableCell>{headlinesCount(d)}</TableCell>
                                                            <TableCell>{d.dos?.length ?? 0}</TableCell>
                                                            <TableCell>{d.donts?.length ?? 0}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                {filtered.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center">
                                                            No tip pools yet. Add one to get started.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filtered.length}
                                        rowsPerPage={rowsPerPage}
                                        page={safePage}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <AstroTipModal isOpen={isOpen} toggle={toggle} selectedAstroTipId={selectedAstroTipId} />
        </Container>
    );
};

export default AstroTips;
